import classNames from 'classnames'
import {memo, useContext} from 'react'
import {graphql, useFragment} from 'react-relay'

import {formatDurationWithMilliseconds} from '../../../../../../utils/dateTimeUtils'
import RowGroup from '../../../../../common/Grid/RowGroup'
import ListItem from '../../../../../common/ListItem/ListItem'
import type {TestScopesKey} from '../../../../../packages/Tests/Tests.types'
import {
  PipelinesTarget,
  PipelinesTargetContext,
} from '../../../../PipelinesPages/contexts/targetContext'

import type {TestScope$key} from './__generated__/TestScope.graphql'
import TestScopeName from './TestScopeName/TestScopeName'

import styles from './TestScope.css'

const fragment = graphql`
  fragment TestScope on TestScope {
    ...TestScopeName
    testOccurrences {
      testCounters {
        failed
        success
        muted
        all
        ignored
        duration
      }
    }
  }
`

type Props = {
  scopeName: TestScopesKey
  scopeKey: TestScope$key
  withCountersColumns?: boolean
}

function TestScope(props: Props) {
  const {scopeName, withCountersColumns} = props
  const pipelineTarget = useContext(PipelinesTargetContext)
  const scope = useFragment(fragment, props.scopeKey)

  const {duration, all, success, failed, muted, ignored} = scope.testOccurrences?.testCounters ?? {}

  return (
    <RowGroup>
      <ListItem
        className={classNames(styles.testScope, {
          [styles.jobDetailsSidebarTarget]: pipelineTarget === PipelinesTarget.JOB_DETAILS_SIDEBAR,
        })}
        rowClassName={styles.testScopeRow}
        highlightHover
        hideBorders
      >
        <div className={styles.nameColumn}>
          <TestScopeName scopeName={scopeName} scopeKey={scope} />
        </div>
        {duration != null && (
          <div className={styles.durationColumn}>{formatDurationWithMilliseconds(duration)}</div>
        )}
        <div className={styles.countColumn} title="Tests">
          {all}
        </div>
        {withCountersColumns ? (
          <>
            <div className={styles.successColumn} title="Success">
              {success || null}
            </div>
            <div className={styles.failureColumn} title="Failure">
              {failed || null}
            </div>
            <div className={styles.mutedColumn} title="Muted">
              {muted || null}
            </div>
            <div className={styles.ignoredColumn} title="Ignored">
              {ignored || null}
            </div>
          </>
        ) : null}
      </ListItem>
    </RowGroup>
  )
}

export default memo(TestScope)
